import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import * as styles from './DragDropZone.module.css';

/**
 * DragDropZone - A component for drag-and-drop functionality
 * 
 * @param {Object} props
 * @param {string} props.label - Label for the drop zone
 * @param {string} props.placeholder - Placeholder text when empty
 * @param {string} props.helpText - Help text shown below the drop zone
 * @param {Array|string} props.value - Current value(s) in the drop zone
 * @param {Function} props.onChange - Callback when value changes
 * @param {Array} props.availableItems - Items available to drag
 * @param {Function} props.renderItem - Custom render function for items
 * @param {boolean} props.multiple - Whether multiple items can be dropped
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.error - Error message to display
 * @param {boolean} props.disabled - Whether the zone is disabled
 * @param {string} props.variant - Visual variant ('default', 'compact')
 */
function DragDropZone({
  label,
  placeholder,
  helpText,
  value,
  onChange,
  // availableItems,  // Not used yet but reserved for future functionality
  renderItem,
  multiple,
  required,
  error,
  disabled,
  variant,
}) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(false);

    const draggedItem = e.dataTransfer.getData('application/json');
    if (draggedItem) {
      try {
        const item = JSON.parse(draggedItem);
        
        if (multiple) {
          const currentValues = Array.isArray(value) ? value : [];
          if (value && !Array.isArray(value)) {
            currentValues.push(value);
          }
          // Don't add if already exists
          const itemValue = item?.value || item;
          const exists = currentValues.some(v => {
            const vValue = v?.value || v;
            return vValue === itemValue;
          });
          if (!exists) {
            onChange([...currentValues, item]);
          }
        } else {
          onChange(item);
        }
      } catch (err) {
        console.error('Failed to parse dropped item:', err);
      }
    }
  }, [disabled, multiple, value, onChange]);

  const handleRemove = useCallback((itemToRemove) => {
    if (disabled) return;

    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const itemToRemoveValue = itemToRemove?.value || itemToRemove;
      onChange(currentValues.filter(v => {
        const vValue = v?.value || v;
        return vValue !== itemToRemoveValue;
      }));
    } else {
      onChange(null);
    }
  }, [disabled, multiple, value, onChange]);

  let normalizedValue = [];
  if (multiple) {
    if (Array.isArray(value)) {
      normalizedValue = value;
    } else if (value) {
      normalizedValue = [value];
    }
  } else if (value) {
    normalizedValue = [value];
  }

  const isEmpty = normalizedValue.length === 0;

  const dropZoneClasses = [
    styles.dropZone,
    variant && styles[variant],
    isDragOver && styles.dragOver,
    error && styles.error,
    disabled && styles.disabled,
  ].filter(Boolean).join(' ');

  return (
    <div className={styles.container}>
      {label && (
        <label className={styles.label} htmlFor="drop-zone">
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div
        id="drop-zone"
        className={dropZoneClasses}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isEmpty ? (
          <div className={styles.placeholder}>
            {placeholder || 'Drag items here'}
          </div>
        ) : (
          <div className={styles.itemList}>
            {normalizedValue.map((item, index) => (
              <div key={typeof item === 'string' ? item : item?.value || index} className={styles.item}>
                {renderItem ? renderItem(item) : (
                  <>
                    <span className={styles.itemText}>
                      {typeof item === 'string' ? item : item?.label || item?.value || 'Unknown'}
                    </span>
                    {!disabled && (
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => handleRemove(item)}
                        aria-label="Remove"
                      >
                        ×
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {(helpText || error) && (
        <div className={error ? styles.errorText : styles.helpText}>
          {error || helpText}
        </div>
      )}
    </div>
  );
}

DragDropZone.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  helpText: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  ]),
  onChange: PropTypes.func.isRequired,
  // availableItems: PropTypes.arrayOf(PropTypes.any), // Reserved for future use
  renderItem: PropTypes.func,
  multiple: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'compact']),
};

DragDropZone.defaultProps = {
  label: '',
  placeholder: '',
  helpText: '',
  value: null,
  // availableItems: [], // Reserved for future use
  renderItem: null,
  multiple: false,
  required: false,
  error: '',
  disabled: false,
  variant: 'default',
};

export default DragDropZone;

